/**
 * Forgot Password Page
 *
 * Request password reset email
 */

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/auth.service.js';
import { HttpClientError } from '../../api/http-client.js';

const forgotPasswordSchema = z.object({
  email: z.string().email('Invalid email address'),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const result = await authService.resetPassword({
        email: data.email,
        redirectUrl: `${window.location.origin}/auth/reset-password`,
      });

      setSuccessMessage(result.message || 'Password reset email sent. Please check your inbox.');
    } catch (error) {
      if (error instanceof HttpClientError) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('Failed to send reset email');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="page forgot-password-page">
      <div className="container">
        <div className="forgot-password-form">
          <h2>Forgot Password</h2>
          <p>Enter your email address and we'll send you a password reset link.</p>

          {successMessage ? (
            <div className="success-state">
              <div className="success-message">{successMessage}</div>
              <button onClick={() => navigate('/login')}>Return to Login</button>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  {...register('email')}
                  disabled={isLoading}
                  autoComplete="email"
                />
                {errors.email && <span className="error">{errors.email.message}</span>}
              </div>

              {errorMessage && <div className="error-message">{errorMessage}</div>}

              <button type="submit" disabled={isLoading}>
                {isLoading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
          )}

          <div className="form-footer">
            <button type="button" onClick={() => navigate('/login')} className="link-button">
              Back to Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
